import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import {
  MdOutlineAdd,
  MdOutlineCelebration,
  MdOutlineChevronRight,
  MdOutlineRefresh,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { EVENTS } from "../config/apiConfig";
import { Badge, Card, EmptyState, ErrorBlock } from "../components/crm/Primitives";
import { SkeletonEventCards } from "../components/crm/Skeletons";
import { formatMoney, formatNumber } from "../components/crm/crmHelpers";
import EventFormModal from "../components/events/EventFormModal";
import {
  EVENT_STATUS_META,
  formatEventDates,
  NO_EVENT_ID,
} from "../components/events/eventHelpers";

/*
  Eventos: a lista dos eventos cadastrados, com quantas clientes vieram por
  cada um. Abrir um evento leva aos cadastros dele (com o botão de cobrar o
  plano), ao cupom e ao vínculo de clientes.

  O cadastro nasce aqui: o evento escolhe o onboarding que serve de filtro e as
  datas; quem se cadastrar por esse onboarding nessas datas entra sozinha.
*/

const REFRESH_MS = 30_000;

const Metric = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {label}
    </dt>
    <dd className="truncate text-lg font-bold leading-tight text-slate-900">
      {value}
    </dd>
  </div>
);

const EventCard = ({ event }) => {
  const status = EVENT_STATUS_META[event.status];
  const { metrics } = event;

  return (
    <Link
      to={`/eventos/${event.id}`}
      className="block rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-pink-200 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="break-words text-base font-bold text-slate-900">
              {event.name}
            </h2>
            <Badge tone={status.tone}>{status.label}</Badge>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            {formatEventDates(event.startsAt, event.endsAt)}
            {event.onboardingLabel ? ` · ${event.onboardingLabel}` : " · Sem onboarding"}
          </p>
        </div>
        <MdOutlineChevronRight size={20} className="shrink-0 text-slate-400" />
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Vieram" value={formatNumber(metrics.clients)} />
        <Metric label="A cobrar" value={formatNumber(metrics.toCharge)} />
        <Metric label="Pagaram" value={formatNumber(metrics.paid)} />
        <Metric label="Recebido" value={formatMoney(metrics.received)} />
      </dl>

      {event.coupon ? (
        <p className="mt-3 text-xs text-slate-500">
          Cupom{" "}
          <span className="font-mono font-semibold text-slate-700">
            {event.coupon.code}
          </span>
          {" · "}
          {formatNumber(metrics.couponRedemptions)} resgate(s)
        </p>
      ) : null}
    </Link>
  );
};

const EventsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const query = useQuery(
    ["events", "list"],
    async () => {
      const { data } = await axios.get(EVENTS.LIST);
      return data.data;
    },
    { refetchInterval: REFRESH_MS, refetchIntervalInBackground: false }
  );

  const events = query.data?.events || [];
  const unassigned = query.data?.unassigned || 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="min-w-0 text-sm text-slate-500">
          Cada evento mede quantas clientes vieram por ele. O onboarding e as
          datas do evento trazem as clientes sozinhas.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
          >
            <MdOutlineRefresh
              size={16}
              className={query.isFetching ? "animate-spin" : ""}
            />
            Atualizar
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-pink-700"
          >
            <MdOutlineAdd size={18} /> Novo evento
          </button>
        </div>
      </div>

      {unassigned > 0 ? (
        <Link
          to={`/eventos/${NO_EVENT_ID}`}
          className="flex items-center justify-between gap-3 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200 transition hover:bg-amber-100"
        >
          <span>
            <strong>{formatNumber(unassigned)}</strong>{" "}
            {unassigned === 1
              ? "cadastro do onboarding do evento ainda está"
              : "cadastros do onboarding do evento ainda estão"}{" "}
            sem evento. Dá para cobrar por lá.
          </span>
          <MdOutlineChevronRight size={18} className="shrink-0" />
        </Link>
      ) : null}

      {query.isLoading ? (
        <SkeletonEventCards />
      ) : query.error ? (
        <ErrorBlock
          message={
            query.error.response?.data?.message ||
            "Não foi possível carregar os eventos."
          }
          onRetry={query.refetch}
        />
      ) : events.length ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={MdOutlineCelebration}
            title="Nenhum evento cadastrado"
            description="Crie o evento com as datas e o onboarding que ele usa. Quem se cadastrar por ele entra sozinha, e cada evento mostra quantas vieram, o cupom e o que foi cobrado."
          />
        </Card>
      )}

      {creating ? (
        <EventFormModal
          onClose={() => setCreating(false)}
          onSaved={(event) => {
            setCreating(false);
            queryClient.invalidateQueries(["events"]);
            navigate(`/eventos/${event.id}`);
          }}
        />
      ) : null}
    </div>
  );
};

export default EventsPage;
